import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import type { LensSpanProps } from '../../types';
import { getColorValue, u } from '../../utilities';

const speed = 2.0;

const sizes = {
  small: {
    totalSize: u(2.25),
    height: u(0.5625),
    dotSize: u(0.375),
    gap: u(0.25),
  },
  medium: {
    totalSize: u(3),
    height: u(0.75),
    dotSize: u(0.5),
    gap: u(0.375),
  },
  large: {
    totalSize: u(6),
    height: u(1.5),
    dotSize: u(1),
    gap: u(0.75),
  },
};

const getTotalSize = (props: EllipsesLoaderWrapperProps) =>
  sizes[props.size].totalSize;
const getHeight = (props: EllipsesLoaderWrapperProps) =>
  sizes[props.size].height;
const getDotSize = (props: EllipsesLoaderWrapperProps) =>
  sizes[props.size].dotSize;
const getGap = (props: EllipsesLoaderWrapperProps) => sizes[props.size].gap;
const getBounceDistance = (props: EllipsesLoaderWrapperProps) =>
  sizes[props.size].dotSize;

const createDotBounce = (bounceDistance: string) => keyframes`
  0%, 40%, 100% {
    transform: translateY(50%);
  }
  20% {
    transform: translateY(calc(50% - ${bounceDistance}));
  }
`;

/** Dot colours for the "ai" variant: blue, purple, yellowDark */
const AI_DOT_COLORS = [
  getColorValue('blue'),
  getColorValue('purple'),
  getColorValue('yellowDark'),
] as const;

const EllipsesLoaderWrapper = styled.span<EllipsesLoaderWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${props => getHeight(props)};
  width: ${props => getTotalSize(props)};
  gap: ${props => getGap(props)};
`;

const Dot = styled.span<DotProps>`
  width: ${props => getDotSize(props)};
  height: ${props => getDotSize(props)};
  border-radius: 50%;
  background-color: ${props =>
    props.color === 'ai' && props.dotIndex !== undefined
      ? AI_DOT_COLORS[props.dotIndex]
      : getColorValue(props.color)};
  transform: translateY(50%);
  animation: ${props => createDotBounce(getBounceDistance(props))} ${speed}s
    ease-in-out infinite;
  animation-fill-mode: both;
  animation-delay: ${props => props.delay}s;
`;

const EllipsesLoader = ({
  color = 'body',
  size = 'medium',
  ...props
}: EllipsesLoaderProps &
  React.ComponentProps<typeof EllipsesLoaderWrapper> &
  Omit<
    LensSpanProps,
    keyof EllipsesLoaderProps &
      React.ComponentProps<typeof EllipsesLoaderWrapper>
  >) => {
  return (
    <EllipsesLoaderWrapper size={size} {...props}>
      <Dot color={color} size={size} delay={0} dotIndex={0} />
      <Dot color={color} size={size} delay={0.2} dotIndex={1} />
      <Dot color={color} size={size} delay={0.4} dotIndex={2} />
    </EllipsesLoaderWrapper>
  );
};

export const availableSizes = Object.keys(sizes);

type EllipsesLoaderProps = {
  color?: string;
  size?: 'small' | 'medium' | 'large';
};

type EllipsesLoaderWrapperProps = {
  size: 'small' | 'medium' | 'large';
};

type DotProps = {
  color?: string;
  size: 'small' | 'medium' | 'large';
  delay?: number;
  dotIndex?: 0 | 1 | 2;
};

export default EllipsesLoader;
